import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { KeyRound, ArrowLeft } from "lucide-react";

export default function ForgotPassword() {
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post("/auth/forgot-password", { email: email.trim() });
            setSent(true);
        } catch {
            // We intentionally always show success to avoid email enumeration
            setSent(true);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-[70vh] flex items-center justify-center px-4 py-12" data-testid="forgot-password-page">
            <div className="w-full max-w-md relative">
                <div className="glow-orb bg-p2w-purple w-[300px] h-[300px] -top-20 -left-20 opacity-30" />
                <div className="relative rounded-2xl border border-p2w-line bg-p2w-surface p-8 shadow-glow-violet">
                    <div className="flex items-center gap-2 mb-2 text-p2w-green">
                        <KeyRound className="w-4 h-4" />
                        <span className="text-[10px] uppercase tracking-[0.25em] font-bold font-sora">Recuperar acceso</span>
                    </div>
                    <h1 className="font-sora font-extrabold text-3xl tracking-tight">¿Olvidaste tu contraseña?</h1>
                    <p className="mt-1 text-p2w-gray text-sm">
                        Ingresa el email asociado a tu cuenta. Te enviaremos un enlace para restablecerla.
                    </p>

                    {!sent ? (
                        <form onSubmit={onSubmit} className="mt-6 space-y-4">
                            <div>
                                <Label className="text-p2w-gray text-xs uppercase tracking-widest">Email</Label>
                                <Input
                                    type="email"
                                    data-testid="forgot-email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    placeholder="tucorreo@ejemplo.com"
                                    className="mt-2 bg-p2w-elevated border-p2w-line text-white focus-visible:ring-p2w-green/40"
                                />
                            </div>
                            <Button
                                type="submit"
                                data-testid="forgot-submit"
                                disabled={submitting}
                                className="w-full bg-p2w-green text-p2w-green-fg hover:bg-p2w-green/90 shadow-glow-green font-semibold py-6 rounded-xl"
                            >
                                {submitting ? "Enviando…" : "Enviar enlace de recuperación"}
                            </Button>
                        </form>
                    ) : (
                        <div className="mt-6 rounded-xl bg-p2w-elevated border border-p2w-line p-5" data-testid="forgot-sent">
                            <p className="text-white font-semibold">Revisa tu bandeja</p>
                            <p className="mt-2 text-sm text-p2w-gray">
                                Si <span className="text-white">{email}</span> está registrado, te llegará un enlace para restablecer la contraseña.
                                El enlace expira en 1 hora.
                            </p>
                            <p className="mt-3 text-xs text-p2w-gray">
                                ¿No lo ves? Revisa tu carpeta de spam o vuelve a intentar en 60 segundos.
                            </p>
                        </div>
                    )}

                    <p className="mt-6 text-sm text-p2w-gray text-center">
                        <Link to="/login" className="inline-flex items-center gap-1 text-p2w-green hover:underline font-semibold">
                            <ArrowLeft className="w-3 h-3" /> Volver a iniciar sesión
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
